import { createContext, type ReactNode, use, useCallback, useEffect, useState } from "react";
import { useAccountDialog } from "@/features/auth/account-dialog";
import { useSession } from "@/features/auth/queries";
import {
  CheckoutReturnNotice,
  type CheckoutReturnNoticeKind,
} from "./components/CheckoutReturnNotice";
import { TopUpDialog } from "./components/TopUpDialog";
import { AUTO_TOP_UP_SUCCESS, useCheckoutBalanceWatch } from "./resume-after-checkout";

interface TopUpContextValue {
  openTopUp: () => void;
}

const TopUpContext = createContext<TopUpContextValue | null>(null);

export function useTopUp(): TopUpContextValue {
  const value = use(TopUpContext);
  if (!value) throw new Error("useTopUp must be used inside TopUpProvider");
  return value;
}

/** One picker for every way into buying credits. */
export function TopUpProvider({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  const { openAccountDialog } = useAccountDialog();
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<CheckoutReturnNoticeKind | null>(null);
  const returned = useCheckoutBalanceWatch();
  const dismissNotice = useCallback(() => setNotice(null), []);

  useEffect(() => {
    if (!returned) return;
    if (returned === "auto_top_up_enabled") setOpen(false);
    setNotice(returned);
  }, [returned]);

  useEffect(() => {
    const onAutoTopUp = () => {
      setOpen(false);
      setNotice("auto_top_up_enabled");
    };
    window.addEventListener(AUTO_TOP_UP_SUCCESS, onAutoTopUp);
    return () => window.removeEventListener(AUTO_TOP_UP_SUCCESS, onAutoTopUp);
  }, []);

  return (
    <TopUpContext
      value={{
        // A guest has nothing to charge, so topping up starts with an account.
        openTopUp: () => (session?.user.isAnonymous ? openAccountDialog() : setOpen(true)),
      }}
    >
      {children}
      <TopUpDialog open={open} onOpenChange={setOpen} />
      {notice && <CheckoutReturnNotice kind={notice} onDismiss={dismissNotice} />}
    </TopUpContext>
  );
}
